import { useState, useEffect, useCallback, useMemo, Fragment } from 'react'
import {
  Grid3x3, RefreshCw, Plus, X, ExternalLink, AlertTriangle, Shield,
  ShieldAlert, CheckCircle2, Trash2, Edit2, Filter, Tag,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── Types ────────────────────────────────────────────────────────────────────

type Score = 1 | 2 | 3

interface AffectedService { id: number; name: string; tier: number; owner?: string | null }

interface Risk {
  id: number | string
  title: string
  detail: string
  category: string
  likelihood: Score
  impact: Score
  priority: number
  cell_label: string
  cell_color: string
  source: string
  source_ref: string
  owner: string
  mitigation: string
  status: string
  url: string | null
  tags: string[]
  affected_services?: AffectedService[]
  created_at: string
  updated_at: string
  review_by: string | null
}

interface Cell {
  likelihood: Score
  impact: Score
  likelihood_label: string
  impact_label: string
  priority: number
  label: string
  color: string
  count: number
  risks: Risk[]
}

interface Matrix {
  cells: Record<string, Cell>
  total: number
  by_priority: Record<string, number>
  by_source: Record<string, number>
  labels: { likelihood: string[]; impact: string[] }
  generated_at: string
}

// ── Colour maps ──────────────────────────────────────────────────────────────

const COLOR_BG: Record<string, string> = {
  emerald: 'bg-emerald/10 border-emerald/30',
  amber:   'bg-amber/10 border-amber/30',
  orange:  'bg-orange/10 border-orange/30',
  rose:    'bg-rose/10 border-rose/30',
}
const COLOR_TEXT: Record<string, string> = {
  emerald: 'text-emerald',
  amber:   'text-amber-light',
  orange:  'text-orange',
  rose:    'text-rose',
}
const COLOR_DOT: Record<string, string> = {
  emerald: 'bg-emerald',
  amber:   'bg-amber',
  orange:  'bg-orange',
  rose:    'bg-rose',
}

const SOURCE_LABEL: Record<string, string> = {
  manual: 'Manual',
  eol:    'EOL',
  cve:    'CVE',
  outage: 'Outage',
}

// ── New/Edit risk form ───────────────────────────────────────────────────────

function RiskForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<Risk>
  onSave: (r: any) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initial?.title || '')
  const [detail, setDetail] = useState(initial?.detail || '')
  const [category, setCategory] = useState(initial?.category || 'operational')
  const [likelihood, setLikelihood] = useState<Score>((initial?.likelihood as Score) || 2)
  const [impact, setImpact] = useState<Score>((initial?.impact as Score) || 2)
  const [owner, setOwner] = useState(initial?.owner || '')
  const [mitigation, setMitigation] = useState(initial?.mitigation || '')
  const [url, setUrl] = useState(initial?.url || '')
  const [tags, setTags] = useState((initial?.tags || []).join(', '))
  const [reviewBy, setReviewBy] = useState(initial?.review_by?.slice(0, 10) || '')
  const [status, setStatus] = useState(initial?.status || 'open')

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
          Title *
        </label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          className="input py-1.5 text-[12px] w-full"
          placeholder="e.g. Key engineer single point of failure"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            Likelihood
          </label>
          <div className="flex gap-1">
            {[1, 2, 3].map(n => (
              <button
                type="button"
                key={n}
                onClick={() => setLikelihood(n as Score)}
                className={clsx(
                  'flex-1 py-1 text-[11px] rounded border transition-colors',
                  likelihood === n
                    ? 'bg-blue/20 border-blue/40 text-blue-light'
                    : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}
              >
                {n === 1 ? 'Low' : n === 2 ? 'Medium' : 'High'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            Impact
          </label>
          <div className="flex gap-1">
            {[1, 2, 3].map(n => (
              <button
                type="button"
                key={n}
                onClick={() => setImpact(n as Score)}
                className={clsx(
                  'flex-1 py-1 text-[11px] rounded border transition-colors',
                  impact === n
                    ? 'bg-blue/20 border-blue/40 text-blue-light'
                    : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}
              >
                {n === 1 ? 'Low' : n === 2 ? 'Medium' : 'High'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
          Detail
        </label>
        <textarea
          value={detail}
          onChange={e => setDetail(e.target.value)}
          rows={2}
          className="input py-1.5 text-[12px] w-full"
          placeholder="What's the risk? What could go wrong?"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            Category
          </label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="input py-1.5 text-[12px] w-full"
            title="Category"
          >
            <option value="operational">Operational</option>
            <option value="security">Security</option>
            <option value="availability">Availability</option>
            <option value="vendor">Vendor</option>
            <option value="compliance">Compliance</option>
            <option value="financial">Financial</option>
            <option value="talent">Talent</option>
            <option value="evergreen">Evergreen / EOL</option>
            <option value="strategic">Strategic</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            Status
          </label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="input py-1.5 text-[12px] w-full"
            title="Status"
          >
            <option value="open">Open</option>
            <option value="monitoring">Monitoring</option>
            <option value="mitigating">Mitigating</option>
            <option value="accepted">Accepted</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
          Mitigation plan
        </label>
        <textarea
          value={mitigation}
          onChange={e => setMitigation(e.target.value)}
          rows={2}
          className="input py-1.5 text-[12px] w-full"
          placeholder="What action is planned?"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            Owner
          </label>
          <input
            value={owner}
            onChange={e => setOwner(e.target.value)}
            className="input py-1.5 text-[12px] w-full"
            placeholder="Person / team"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            Review by
          </label>
          <input
            type="date"
            value={reviewBy}
            onChange={e => setReviewBy(e.target.value)}
            className="input py-1.5 text-[12px] w-full"
            title="Review-by date"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            URL
          </label>
          <input
            value={url || ''}
            onChange={e => setUrl(e.target.value)}
            className="input py-1.5 text-[12px] w-full"
            placeholder="https://"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
            Tags (comma-separated)
          </label>
          <input
            value={tags}
            onChange={e => setTags(e.target.value)}
            className="input py-1.5 text-[12px] w-full"
            placeholder="aws, us-east-1, single-region"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSave({
            title, detail, category, likelihood, impact, owner, mitigation, status, url,
            tags: tags.split(',').map(t => t.trim()).filter(Boolean),
            review_by: reviewBy || null,
          })}
          className="btn-primary text-xs"
          disabled={!title.trim()}
        >
          Save risk
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Risk chip inside a cell ──────────────────────────────────────────────────

function RiskChip({ risk, onClick }: { risk: Risk; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'block w-full text-left px-2 py-1 rounded text-[10px] border transition-colors',
        COLOR_BG[risk.cell_color],
        'hover:brightness-125'
      )}
      title={risk.title}
    >
      <div className="flex items-start gap-1.5">
        <span className={clsx('w-1 h-1 rounded-full mt-1 shrink-0', COLOR_DOT[risk.cell_color])} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-text-primary truncate">{risk.title}</div>
          <div className="flex items-center gap-1 text-[9px] text-text-muted">
            <span className={COLOR_TEXT[risk.cell_color]}>{SOURCE_LABEL[risk.source] || risk.source}</span>
            {risk.owner && <>· {risk.owner}</>}
          </div>
        </div>
      </div>
    </button>
  )
}

// ── Risk detail drawer ───────────────────────────────────────────────────────

function RiskDetail({
  risk,
  onClose,
  onEdit,
  onDelete,
}: {
  risk: Risk
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const isManual = risk.source === 'manual'
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-surface-card border-l border-border shadow-2xl z-40 overflow-y-auto">
      <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={clsx('w-2 h-2 rounded-full', COLOR_DOT[risk.cell_color])} />
          <span className={clsx('text-[10px] font-semibold uppercase tracking-wider', COLOR_TEXT[risk.cell_color])}>
            {risk.cell_label} · Priority {risk.priority}
          </span>
        </div>
        <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" title="Close">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <h2 className="text-base font-bold text-text-primary leading-snug">{risk.title}</h2>

        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Likelihood</div>
            <div className="text-text-primary font-semibold">
              {risk.likelihood === 1 ? 'Low' : risk.likelihood === 2 ? 'Medium' : 'High'} ({risk.likelihood}/3)
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Impact</div>
            <div className="text-text-primary font-semibold">
              {risk.impact === 1 ? 'Low' : risk.impact === 2 ? 'Medium' : 'High'} ({risk.impact}/3)
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Source</div>
            <div className="text-text-primary">{SOURCE_LABEL[risk.source] || risk.source}</div>
            {risk.source_ref && <div className="text-[10px] text-text-muted font-mono">{risk.source_ref}</div>}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Category</div>
            <div className="text-text-primary capitalize">{risk.category}</div>
          </div>
          {risk.owner && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-text-muted">Owner</div>
              <div className="text-text-primary">{risk.owner}</div>
            </div>
          )}
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Status</div>
            <div className="text-text-primary capitalize">{risk.status}</div>
          </div>
          {risk.review_by && (
            <div className="col-span-2">
              <div className="text-[9px] uppercase tracking-wider text-text-muted">Review by</div>
              <div className="text-text-primary">{risk.review_by.slice(0, 10)}</div>
            </div>
          )}
        </div>

        {risk.detail && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Detail</div>
            <p className="text-[11px] text-text-secondary leading-relaxed whitespace-pre-wrap">{risk.detail}</p>
          </div>
        )}

        {risk.mitigation && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Mitigation</div>
            <p className="text-[11px] text-text-secondary leading-relaxed whitespace-pre-wrap">{risk.mitigation}</p>
          </div>
        )}

        {risk.tags.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
              <Tag size={10} /> Tags
            </div>
            <div className="flex flex-wrap gap-1">
              {risk.tags.map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-surface-raised text-text-secondary">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {risk.affected_services && risk.affected_services.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
              <ShieldAlert size={10} /> Affected services
              <span className="text-text-secondary">({risk.affected_services.length})</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {risk.affected_services.map(s => (
                <span key={s.id}
                  className={clsx(
                    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border',
                    s.tier === 1 ? 'bg-rose/10 border-rose/30 text-rose'
                      : s.tier === 2 ? 'bg-amber/10 border-amber/30 text-amber-light'
                      : 'bg-emerald/10 border-emerald/30 text-emerald'
                  )}
                  title={`Tier ${s.tier}${s.owner ? ` · ${s.owner}` : ''}`}
                >
                  <span className="font-mono font-semibold">T{s.tier}</span>
                  <span>{s.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {risk.url && (
          <a
            href={risk.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1"
          >
            Reference <ExternalLink size={10} />
          </a>
        )}

        {isManual && (
          <div className="pt-3 border-t border-border flex gap-2">
            <button type="button" onClick={onEdit} className="btn-secondary text-xs">
              <Edit2 size={11} /> Edit
            </button>
            <button
              type="button"
              onClick={() => { if (confirm(`Delete risk "${risk.title}"?`)) onDelete() }}
              className="btn-ghost text-xs text-rose"
            >
              <Trash2 size={11} /> Delete
            </button>
          </div>
        )}

        {!isManual && (
          <div className="pt-3 border-t border-border">
            <p className="text-[10px] text-text-muted flex items-start gap-1.5">
              <Shield size={11} className="shrink-0 mt-0.5" />
              Auto-derived risk from {SOURCE_LABEL[risk.source] || risk.source}. Updates automatically when
              source data changes — not editable.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── CSV export ───────────────────────────────────────────────────────────────

function exportRisksCsv(matrix: Matrix | null) {
  if (!matrix) return
  const rows: Risk[] = []
  for (const cell of Object.values(matrix.cells || {})) rows.push(...(cell.risks || []))
  const cols: [string, (r: Risk) => string | number][] = [
    ['priority',   r => r.priority],
    ['category',   r => r.category],
    ['title',      r => r.title],
    ['detail',     r => r.detail],
    ['likelihood', r => r.likelihood],
    ['impact',     r => r.impact],
    ['cell',       r => r.cell_label],
    ['status',     r => r.status],
    ['owner',      r => r.owner],
    ['source',     r => r.source],
    ['source_ref', r => r.source_ref],
    ['mitigation', r => r.mitigation],
    ['url',        r => r.url || ''],
    ['tags',       r => (r.tags || []).join('|')],
    ['review_by',  r => r.review_by || ''],
    ['updated_at', r => r.updated_at],
  ]
  const esc = (v: string | number) => {
    const s = String(v ?? '')
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const header = cols.map(c => c[0]).join(',')
  const body = rows.map(r => cols.map(c => esc(c[1](r))).join(',')).join('\n')
  const csv = `${header}\n${body}\n`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `risk-matrix-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function RiskMatrixPage() {
  const [matrix, setMatrix] = useState<Matrix | null>(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Risk | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Risk | null>(null)
  const [sourceFilter, setSourceFilter] = useState<string>('')

  const fetchMatrix = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/risks/matrix')
      setMatrix(data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchMatrix() }, [fetchMatrix])

  const saveRisk = async (risk: any) => {
    if (editing) {
      await api.put(`/api/risks/${editing.id}`, risk)
    } else {
      await api.post('/api/risks', risk)
    }
    setShowForm(false)
    setEditing(null)
    setSelected(null)
    fetchMatrix()
  }

  const deleteRisk = async (id: number | string) => {
    if (typeof id === 'string' && id.startsWith('auto-')) return
    await api.delete(`/api/risks/${id}`)
    setSelected(null)
    fetchMatrix()
  }

  const filteredCells = useMemo(() => {
    if (!matrix) return null
    if (!sourceFilter) return matrix.cells
    const out: Record<string, Cell> = {}
    for (const [k, c] of Object.entries(matrix.cells)) {
      out[k] = { ...c, risks: c.risks.filter(r => r.source === sourceFilter) }
      out[k].count = out[k].risks.length
    }
    return out
  }, [matrix, sourceFilter])

  if (loading && !matrix) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }
  if (!matrix || !filteredCells) return <div className="text-text-muted text-sm">No data</div>

  const likelihoodLabels = matrix.labels.likelihood
  const impactLabels = matrix.labels.impact
  const filteredTotal = Object.values(filteredCells).reduce((s, c) => s + c.count, 0)

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Grid3x3 size={20} className="text-rose" /> Risk Matrix (9-box)
          </h1>
          <p className="text-[10px] text-text-secondary">
            {filteredTotal} risks plotted — auto-derived from EOL, CVE, outages + manual entries
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchMatrix} className="btn-ghost text-xs" title="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => exportRisksCsv(matrix)}
            className="btn-ghost text-xs"
            title="Download all risks as CSV"
          >
            ↓ CSV
          </button>
          <button
            type="button"
            onClick={() => { setEditing(null); setShowForm(true) }}
            className="btn-primary text-xs"
          >
            <Plus size={12} /> New risk
          </button>
        </div>
      </div>

      {/* Priority breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { p: 5, color: 'rose',    label: 'Critical' },
          { p: 4, color: 'orange',  label: 'Mitigate' },
          { p: 3, color: 'amber',   label: 'Monitor' },
          { p: 2, color: 'emerald', label: 'Accept' },
          { p: 1, color: 'emerald', label: 'Low' },
        ].map(b => (
          <div key={b.p} className={clsx('card p-2.5 border', COLOR_BG[b.color])}>
            <div className="flex items-center justify-between">
              <span className={clsx('text-[10px] uppercase tracking-wider font-semibold', COLOR_TEXT[b.color])}>
                P{b.p} — {b.label}
              </span>
              <span className={clsx('text-lg font-bold', COLOR_TEXT[b.color])}>
                {matrix.by_priority[b.p] || 0}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Source filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <button
          type="button"
          onClick={() => setSourceFilter('')}
          className={clsx(
            'px-2 py-1 text-[10px] rounded border transition-colors',
            !sourceFilter
              ? 'bg-blue/10 border-blue/30 text-blue-light'
              : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
          )}
        >
          All ({matrix.total})
        </button>
        {Object.entries(matrix.by_source).map(([src, count]) => (
          <button
            type="button"
            key={src}
            onClick={() => setSourceFilter(sourceFilter === src ? '' : src)}
            className={clsx(
              'px-2 py-1 text-[10px] rounded border transition-colors',
              sourceFilter === src
                ? 'bg-blue/10 border-blue/30 text-blue-light'
                : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
            )}
          >
            {SOURCE_LABEL[src] || src} ({count})
          </button>
        ))}
      </div>

      {/* 9-box grid */}
      <div className="card p-4">
        <div className="flex gap-2">
          {/* Y-axis label */}
          <div className="flex items-center">
            <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
              Impact →
            </div>
          </div>
          <div className="flex-1">
            {/* Y-axis tick labels + grid */}
            <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-2">
              {/* Top row: high impact */}
              {[3, 2, 1].map(impact => (
                <Fragment key={`row-${impact}`}>
                  <div className="flex items-center justify-end pr-1 text-[10px] text-text-muted">
                    <span className="uppercase tracking-wider font-semibold">{impactLabels[impact - 1]}</span>
                  </div>
                  {[1, 2, 3].map(likelihood => {
                    const cell = filteredCells[`${likelihood}-${impact}`]
                    return (
                      <div
                        key={`${likelihood}-${impact}`}
                        className={clsx(
                          'rounded-md border p-2 min-h-[120px] transition-colors',
                          COLOR_BG[cell.color],
                          'hover:brightness-110'
                        )}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={clsx('text-[9px] uppercase tracking-wider font-semibold', COLOR_TEXT[cell.color])}>
                            {cell.label}
                          </span>
                          <span className={clsx('text-xs font-bold', COLOR_TEXT[cell.color])}>
                            {cell.count}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {cell.risks.slice(0, 5).map(r => (
                            <RiskChip key={r.id} risk={r} onClick={() => setSelected(r)} />
                          ))}
                          {cell.risks.length > 5 && (
                            <div className="text-[9px] text-text-muted text-center py-0.5">
                              +{cell.risks.length - 5} more
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </Fragment>
              ))}
              {/* X-axis tick labels */}
              <div />
              {[1, 2, 3].map(likelihood => (
                <div key={`x-${likelihood}`} className="text-center text-[10px] text-text-muted">
                  <span className="uppercase tracking-wider font-semibold">{likelihoodLabels[likelihood - 1]}</span>
                </div>
              ))}
            </div>

            {/* X-axis title */}
            <div className="text-center mt-2">
              <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">
                Likelihood →
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Create/edit form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card border border-border rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-primary">
                {editing ? 'Edit risk' : 'New risk'}
              </h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null) }} className="text-text-muted hover:text-text-primary" title="Close">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <RiskForm
                initial={editing || undefined}
                onSave={saveRisk}
                onCancel={() => { setShowForm(false); setEditing(null) }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <RiskDetail
          risk={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setShowForm(true); setSelected(null) }}
          onDelete={() => deleteRisk(selected.id)}
        />
      )}

      {/* Footer legend */}
      <div className="card p-3 text-[10px] text-text-muted">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="uppercase tracking-wider font-semibold">Priority scale:</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose" /> P5 Critical</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange" /> P4 Mitigate</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber" /> P3 Monitor</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald" /> P1-P2 Accept</span>
          <span className="ml-auto">Generated {matrix.generated_at.slice(0, 16)}</span>
        </div>
      </div>
    </div>
  )
}
